import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';

const THEME_STORAGE_KEY = 'rahber_theme';

const ThemeContext = createContext({
    theme: 'light',
    isDark: false,
    toggleTheme: () => {},
    setTheme: () => {},
});

export const ThemeProvider = ({ children }) => {
    const [theme, setThemeState] = useState(() => {
        try {
            const saved = localStorage.getItem(THEME_STORAGE_KEY);
            if (saved === 'marquee' || saved === 'dark' || saved === 'light') {
                return saved;
            }
        } catch {
            // ignore localStorage error
        }
        return 'light';
    });

    useEffect(() => {
        try {
            localStorage.setItem(THEME_STORAGE_KEY, theme);
        } catch {
            // ignore localStorage error
        }

        const root = document.documentElement;
        if (theme === 'marquee' || theme === 'dark') {
            root.setAttribute('data-theme', 'dark');
            root.setAttribute('data-theme-variant', 'marquee');
            root.classList.add('dark');
            root.classList.add('theme-marquee');
        } else {
            root.setAttribute('data-theme', 'light');
            root.setAttribute('data-theme-variant', 'light');
            root.classList.remove('dark');
            root.classList.remove('theme-marquee');
        }

        if (document.body) {
            document.body.setAttribute('data-theme', theme === 'marquee' ? 'dark' : theme);
        }
    }, [theme]);

    const toggleTheme = useCallback(() => {
        setThemeState((prev) => (prev === 'marquee' || prev === 'dark' ? 'light' : 'marquee'));
    }, []);

    const setTheme = useCallback((newTheme) => {
        if (newTheme === 'marquee' || newTheme === 'dark' || newTheme === 'light') {
            setThemeState(newTheme);
        }
    }, []);

    const isDark = theme === 'marquee' || theme === 'dark';
    const isMarquee = theme === 'marquee';

    const value = useMemo(
        () => ({
            theme,
            isDark,
            isMarquee,
            toggleTheme,
            setTheme,
        }),
        [theme, isDark, isMarquee, toggleTheme, setTheme]
    );

    return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};

export default ThemeContext;
